/**
 * TorqWars — GSAP smooth / reduced scroll for the site scroll container.
 * Desktop: lerps `.page-wrapper` wheel input via gsap.ticker.
 * Mobile: native document scroll (no wheel interception).
 */
(function () {
  'use strict';

  var HOME_PAGE = document.body.classList.contains('template-home');

  /* iOS (Safari/Chrome) shows and hides the address bar and bottom toolbar as the page scrolls
     up and down, which fires height-only resizes. Without this, ScrollTrigger refreshes every
     pin mid-scroll and the page jumps on scroll and reverse scroll. Width changes (rotation)
     still refresh. Runs before any section script (this file loads right after ScrollTrigger). */
  if (window.ScrollTrigger && typeof window.ScrollTrigger.config === 'function') {
    window.ScrollTrigger.config({ ignoreMobileResize: true });
  }

  var DESKTOP_QUERY = window.matchMedia('(min-width: 990px)');
  var REDUCED_MOTION_QUERY = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {{
   *   ready: boolean,
   *   active: boolean,
   *   container: HTMLElement | null,
   *   current: number,
   *   target: number,
   *   ease: number,
   *   wheelSmoothing: boolean,
   *   programmatic: boolean,
   *   ticker: (() => void) | null,
   *   proxyBound: boolean,
   * }} */
  var state = {
    ready: false,
    active: false,
    container: null,
    current: 0,
    target: 0,
    ease: 0.08,
    defaultEase: 0.08,
    scrubEase: 0.16,
    scrubMode: false,
    wheelSmoothing: false,
    programmatic: false,
    ticker: null,
    proxyBound: false,
  };

  /**
   * @returns {Window & { gsap?: { ticker: { add: (fn: () => void) => void; remove: (fn: () => void) => void } }; ScrollTrigger?: { registerPlugin: (plugin: unknown) => void; scrollerProxy: (el: Element, config: Record<string, unknown>) => void; update: () => void; refresh: () => void } }}
   */
  function getWin() {
    return window;
  }

  function isDesignMode() {
    return document.documentElement.classList.contains('shopify-design-mode');
  }

  function isScrollLocked() {
    return (
      document.documentElement.hasAttribute('scroll-lock') ||
      document.documentElement.classList.contains('tw-preloader-active')
    );
  }

  /**
   * @returns {HTMLElement}
   */
  function getScrollContainer() {
    if (DESKTOP_QUERY.matches) {
      var pageWrapper = document.querySelector('.page-wrapper');
      if (pageWrapper instanceof HTMLElement) return pageWrapper;
    }
    return document.scrollingElement || document.documentElement;
  }

  function getMaxScroll() {
    if (!state.container) return 0;
    return Math.max(0, state.container.scrollHeight - state.container.clientHeight);
  }

  function clampTarget() {
    state.target = Math.max(0, Math.min(state.target, getMaxScroll()));
  }

  /**
   * @param {EventTarget | null} target
   * @param {number} deltaY
   * @returns {HTMLElement | null}
   */
  /**
   * Whether an element's overflow-y lets it scroll. Cached: computed style is
   * read once per element, not on every wheel event — reading it there forced
   * a full style recalc whenever an animation had touched styles that frame.
   * @type {WeakMap<Element, boolean>}
   */
  var scrollableOverflow = new WeakMap();

  /**
   * @param {HTMLElement} node
   * @returns {boolean}
   */
  function hasScrollableOverflow(node) {
    var cached = scrollableOverflow.get(node);
    if (cached !== undefined) return cached;
    var overflowY = window.getComputedStyle(node).overflowY;
    var result = overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay';
    scrollableOverflow.set(node, result);
    return result;
  }

  function findBlockingScrollParent(target, deltaY) {
    if (!(target instanceof Element) || !state.container) return null;

    var node = target;
    while (node && node !== state.container) {
      if (node instanceof HTMLElement) {
        if (hasScrollableOverflow(node) && node.scrollHeight > node.clientHeight + 1) {
          var maxTop = node.scrollHeight - node.clientHeight;
          if (deltaY > 0 && node.scrollTop < maxTop - 1) return node;
          if (deltaY < 0 && node.scrollTop > 1) return node;
        }
      }
      node = node.parentElement;
    }

    return null;
  }

  function onTicker(time, deltaTime) {
    if (!state.active || !state.container) return;

    // Home: do not read layout on every display tick while nothing is moving.
    if (HOME_PAGE && !state.wheelSmoothing && state.current === state.target) return;
    clampTarget();
    var diff = state.target - state.current;

    if (Math.abs(diff) < 0.5) {
      state.current = state.target;
      state.wheelSmoothing = false;
    } else {
      var ease = HOME_PAGE
        ? 1 - Math.pow(1 - state.ease, Math.min(deltaTime || 16.667, 64) / 16.667)
        : state.ease;
      state.current += diff * ease;
      state.wheelSmoothing = true;
    }

    if (Math.abs(state.container.scrollTop - state.current) > 0.1) {
      state.programmatic = true;
      state.container.scrollTop = state.current;
      state.programmatic = false;

      var ScrollTrigger = getWin().ScrollTrigger;
      if (ScrollTrigger) ScrollTrigger.update();
    }
  }

  /** @param {WheelEvent} event */
  function onWheel(event) {
    if (!state.active || !state.container || isScrollLocked() || isDesignMode()) return;
    if (!DESKTOP_QUERY.matches) return;
    if (Math.abs(event.deltaY) < Math.abs(event.deltaX)) return;
    if (findBlockingScrollParent(event.target, event.deltaY)) return;

    event.preventDefault();

    var deltaY = event.deltaY;
    if (HOME_PAGE && event.deltaMode === 1) deltaY *= 16;
    if (HOME_PAGE && event.deltaMode === 2) deltaY *= state.container.clientHeight;

    // Homepage wheel input is rendered once per display tick, including the
    // flag. Painting inside each wheel event causes uneven frame delivery.
    if (state.scrubMode && !HOME_PAGE) {
      state.target += deltaY;
      clampTarget();
      state.current = state.target;
      state.wheelSmoothing = false;
      state.programmatic = true;
      state.container.scrollTop = state.current;
      state.programmatic = false;
      var ScrollTrigger = getWin().ScrollTrigger;
      if (ScrollTrigger) ScrollTrigger.update();
      return;
    }

    state.wheelSmoothing = true;
    state.target += deltaY;
    // onTicker clamps once per frame. Clamping here read scrollHeight and
    // forced a page layout on every wheel event (several per frame on trackpads).
  }

  function onContainerScroll() {
    if (!state.container || state.programmatic) return;

    var ScrollTrigger = getWin().ScrollTrigger;
    if (ScrollTrigger) ScrollTrigger.update();

    if (!state.wheelSmoothing) {
      state.current = state.container.scrollTop;
      state.target = state.container.scrollTop;
    }
  }

  /**
   * @param {number} value
   * @param {boolean} immediate
   */
  function setScrollTarget(value, immediate) {
    state.target = value;
    clampTarget();

    if (immediate) {
      state.current = state.target;
      state.wheelSmoothing = false;
      if (state.container) {
        state.programmatic = true;
        state.container.scrollTop = state.current;
        state.programmatic = false;
      }
    }
  }

  function ensureScrollerProxy() {
    var win = getWin();
    var ScrollTrigger = win.ScrollTrigger;
    if (!ScrollTrigger) return;

    state.container = getScrollContainer();
    if (!(state.container instanceof HTMLElement)) return;

    /** @type {HTMLElement} */
    var scrollRoot = state.container;

    // Native document scrolling already has ScrollTrigger's viewport handling.
    // Proxying <html> competes with window scroll and jumps a pin on refresh.
    var scrollingEl = document.scrollingElement || document.documentElement;
    if (!DESKTOP_QUERY.matches || scrollRoot === scrollingEl || scrollRoot === document.body) {
      window.__torqwarsScrollerProxy = true;
      return;
    }

    ScrollTrigger.scrollerProxy(scrollRoot, {
      scrollTop: function (/** @type {number} */ value) {
        if (arguments.length) {
          if (state.active) {
            setScrollTarget(value, false);
          } else {
            scrollRoot.scrollTop = value;
          }
        }
        return state.active ? state.current : scrollRoot.scrollTop;
      },
      getBoundingClientRect: function () {
        return {
          top: 0,
          left: 0,
          width: scrollRoot.clientWidth,
          height: scrollRoot.clientHeight,
        };
      },
    });

    if (!state.proxyBound) {
      scrollRoot.addEventListener('scroll', function () {
        if (HOME_PAGE && state.active) return;
        if (win.ScrollTrigger) win.ScrollTrigger.update();
      });
      state.proxyBound = true;
    }

    window.__torqwarsScrollerProxy = true;
  }

  function refreshMetrics() {
    if (!state.container) state.container = getScrollContainer();
    clampTarget();

    if (state.container && !state.wheelSmoothing) {
      state.current = state.container.scrollTop;
      state.target = state.container.scrollTop;
    }

    var ScrollTrigger = getWin().ScrollTrigger;
    if (ScrollTrigger) ScrollTrigger.refresh();
  }

  var refreshScheduled = false;
  function scheduleRefresh() {
    if (refreshScheduled) return;
    refreshScheduled = true;
    window.requestAnimationFrame(function () {
      refreshScheduled = false;
      refreshMetrics();
    });
  }

  function activate() {
    if (state.active || REDUCED_MOTION_QUERY.matches || isDesignMode()) return;

    state.container = getScrollContainer();
    if (!(state.container instanceof HTMLElement)) return;

    state.current = state.container.scrollTop;
    state.target = state.current;

    document.documentElement.classList.add('tw-gsap-smooth-scroll');
    state.container.style.scrollBehavior = 'auto';

    state.ticker = onTicker;
    var gsap = getWin().gsap;
    if (gsap) gsap.ticker.add(state.ticker);

    window.addEventListener('wheel', onWheel, { passive: false, capture: true });
    state.container.addEventListener('scroll', onContainerScroll, { passive: true });

    if (!HOME_PAGE) {
      DESKTOP_QUERY.addEventListener('change', onBreakpointChange);
      REDUCED_MOTION_QUERY.addEventListener('change', onReducedMotionChange);
    }

    var win = getWin();
    if (win.gsap && win.ScrollTrigger) {
      win.gsap.registerPlugin(win.ScrollTrigger);
    }

    ensureScrollerProxy();
    state.active = true;
    state.ready = true;
    refreshMetrics();
  }

  function deactivate() {
    if (!state.active) return;

    state.active = false;
    document.documentElement.classList.remove('tw-gsap-smooth-scroll');

    if (state.ticker) {
      var gsap = getWin().gsap;
      if (gsap) gsap.ticker.remove(state.ticker);
      state.ticker = null;
    }

    window.removeEventListener('wheel', onWheel, true);
    if (state.container) {
      state.container.removeEventListener('scroll', onContainerScroll);
    }

    if (!HOME_PAGE) {
      DESKTOP_QUERY.removeEventListener('change', onBreakpointChange);
      REDUCED_MOTION_QUERY.removeEventListener('change', onReducedMotionChange);
    }

    ensureScrollerProxy();
  }

  function onBreakpointChange() {
    deactivate();
    init();
  }

  function onReducedMotionChange() {
    if (REDUCED_MOTION_QUERY.matches) deactivate();
    else init();
  }

  /**
   * @param {() => void} callback
   */
  function whenGsapReady(callback) {
    var win = getWin();
    if (win.gsap && win.ScrollTrigger) {
      callback();
      return;
    }

    var attempts = 0;
    var timer = window.setInterval(function () {
      var currentWin = getWin();
      if (currentWin.gsap && currentWin.ScrollTrigger) {
        window.clearInterval(timer);
        callback();
      } else if (++attempts > 160) {
        window.clearInterval(timer);
      }
    }, 50);
  }

  function init() {
    state.container = getScrollContainer();

    if (REDUCED_MOTION_QUERY.matches || isDesignMode()) {
      state.ready = true;
      ensureScrollerProxy();
      return;
    }

    // Mobile uses native document scroll — only lerp wheel input on desktop.
    if (!DESKTOP_QUERY.matches) {
      state.ready = true;
      ensureScrollerProxy();
      return;
    }

    if (document.documentElement.classList.contains('tw-preloader-active')) {
      state.ready = true;
      ensureScrollerProxy();
      window.addEventListener(
        'tw-preloader-finished',
        function onPreloaderFinished() {
          window.removeEventListener('tw-preloader-finished', onPreloaderFinished);
          activate();
        },
        { once: true }
      );
      return;
    }

    activate();
  }

  function activateAfterPreloader() {
    if (state.active || REDUCED_MOTION_QUERY.matches || isDesignMode()) {
      resync();
      scheduleRefresh();
      return;
    }

    if (!DESKTOP_QUERY.matches) {
      resync();
      scheduleRefresh();
      return;
    }

    activate();
    if (!HOME_PAGE) setScrubMode(true);
    resync();
    scheduleRefresh();
  }

  /**
   * @param {number} top
   * @param {{ immediate?: boolean } | undefined} options
   */
  function scrollTo(top, options) {
    if (!state.container) state.container = getScrollContainer();
    var immediate = Boolean(options && options.immediate);

    if (!state.active || immediate) {
      setScrollTarget(top, true);
      return;
    }

    setScrollTarget(top, false);
  }

  function freeze() {
    resync();
  }

  function resync() {
    if (!state.container) state.container = getScrollContainer();
    if (!state.container) return;
    state.current = state.container.scrollTop;
    state.target = state.current;
    state.wheelSmoothing = false;
  }

  /**
   * @param {boolean} active
   */
  function setScrubMode(active) {
    var next = Boolean(active);
    if (state.scrubMode === next) return;
    state.scrubMode = next;
    state.ease = next ? state.scrubEase : state.defaultEase;
    // Changing interpolation speed must not discard wheel input still in flight.
    // Layout changes and explicit scroll resets call resync separately.
    if (!HOME_PAGE) resync();
  }

  window.TorqWarsSmoothScroll = {
    ensureScrollerProxy: ensureScrollerProxy,
    scrollTo: scrollTo,
    freeze: freeze,
    resync: resync,
    activateAfterPreloader: activateAfterPreloader,
    setScrubMode: setScrubMode,
    getScrollTop: function () {
      if (state.active) return state.current;
      if (state.container) return state.container.scrollTop;
      return window.scrollY;
    },
    getContainer: getScrollContainer,
    refresh: refreshMetrics,
    scheduleRefresh: scheduleRefresh,
    isActive: function () {
      return state.active;
    },
    setEase: function (value) {
      state.defaultEase = Math.max(0.04, Math.min(0.35, value));
      if (!state.scrubMode) {
        state.ease = state.defaultEase;
      }
    },
  };

  var lastInnerWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    var nextWidth = window.innerWidth;
    var widthChanged = Math.abs(nextWidth - lastInnerWidth) > 1;
    lastInnerWidth = nextWidth;
    // Mobile address-bar show/hide fires resize without a width change and
    // a ScrollTrigger refresh in the middle of a pin yanks the page.
    if (!DESKTOP_QUERY.matches && !widthChanged) return;
    window.clearTimeout(window.__twSmoothScrollResize);
    window.__twSmoothScrollResize = window.setTimeout(scheduleRefresh, 150);
  });

  window.addEventListener('pageshow', function () {
    if (typeof window.__torqwarsScheduleScrollRestore === 'function') {
      window.__torqwarsScheduleScrollRestore();
    }
  });

  if (HOME_PAGE) {
    DESKTOP_QUERY.addEventListener('change', onBreakpointChange);
    REDUCED_MOTION_QUERY.addEventListener('change', onReducedMotionChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      whenGsapReady(init);
    });
  } else {
    whenGsapReady(init);
  }
})();
